import { marked } from "marked";
import DOMPurify from "dompurify";

// Configure marked for GFM
marked.setOptions({
  gfm: true,
  breaks: true,
});

/**
 * Generate a URL-safe slug from heading text.
 * Matches the slugify logic used by the TOC extractor in the docs page.
 */
function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Add id attributes to <h1>–<h6> elements that don't already have one.
 * This enables anchor links and TOC navigation.
 */
export function addHeadingIds(html: string): string {
  return html.replace(
    /<h([1-6])([^>]*)>([\s\S]*?)<\/h\1>/gi,
    (match, level: string, attrs: string, content: string) => {
      // If id already exists, leave the heading untouched
      if (/\sid\s*=/.test(attrs)) return match;
      const textContent = content.replace(/<[^>]*>/g, "").trim();
      const id = slugifyHeading(textContent);
      if (!id) return match;
      return `<h${level} id="${id}"${attrs}>${content}</h${level}>`;
    }
  );
}

/**
 * Strip a leading <h1> from content if it matches (or closely resembles) the
 * given title.  Prevents duplicate headings when the page already renders the
 * title in its own header.
 */
export function stripLeadingTitle(content: string, title: string): string {
  if (!content || !title) return content || "";
  const titleLower = title.toLowerCase().trim();

  // Match a leading <h1>...</h1> (with optional attributes)
  const match = content.match(/^[\s\S]*?<h1[^>]*>([\s\S]*?)<\/h1>/i);
  if (match) {
    const headingText = match[1].replace(/<[^>]*>/g, "").toLowerCase().trim();
    if (headingText === titleLower || headingText.length > 0 && titleLower.includes(headingText)) {
      // Remove the leading h1
      return content.slice(match[0].length).replace(/^\s+/, "");
    }
  }

  // Also handle markdown: leading "# Title" or "# Title\n"
  const mdMatch = content.match(/^#{1}\s+(.+?)(\n|$)/);
  if (mdMatch) {
    const headingText = mdMatch[1].trim().toLowerCase();
    if (headingText === titleLower || headingText.length > 0 && titleLower.includes(headingText)) {
      return content.slice(mdMatch[0].length).replace(/^\s+/, "");
    }
  }

  return content;
}

/** Real HTML block/inline tags an HTML (CMS) body is built from. */
const HTML_TAG =
  /<\/?(p|div|span|h[1-6]|ul|ol|li|a|img|br|hr|strong|em|b|i|u|pre|code|blockquote|table|thead|tbody|tr|th|td|figure|figcaption|iframe|section|article|header|footer|nav|video|source|picture)(\s[^>]*)?\/?>/i;

// Fenced code blocks (``` or ~~~) and inline code spans. \u0060 is a backtick.
const FENCED_CODE = new RegExp("^(\\u0060{3,}|~{3,})[\\s\\S]*?^\\1", "gm");
const INLINE_CODE = new RegExp("\\u0060[^\\u0060\\n]*\\u0060", "g");

/**
 * True when the body is HTML rather than Markdown.
 *
 * Markdown docs are full of placeholders like "mc run --model <name>" or
 * "/plan <title>". A plain "any <tag>" test took those for HTML, skipped the
 * Markdown parser and the page showed raw "|---|" and "**bold**" text. So:
 * ignore anything inside code (fenced blocks and inline spans), and only count
 * real HTML tag names.
 */
export function looksLikeHtml(content: string): boolean {
  const prose = content.replace(FENCED_CODE, "").replace(INLINE_CODE, "");
  return HTML_TAG.test(prose);
}

/**
 * Auto-detect and render content that may be HTML, Markdown, or plain text.
 * Sanitizes output with DOMPurify to prevent XSS.
 * Adds id attributes to headings for anchor/TOC navigation.
 */
export function renderContentToHtml(content: string): string {
  if (!content) return "";

  let html: string;
  if (looksLikeHtml(content)) {
    html = content;
  } else {
    html = marked.parse(content) as string;
  }

  // Add id attributes to headings for TOC navigation
  html = addHeadingIds(html);

  return DOMPurify.sanitize(html, {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["target", "allow", "allowfullscreen", "frameborder", "id"],
  });
}
